import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { ROLE_LABEL } from "@/lib/format";
import { useState, useEffect, Suspense } from "react";
import ForcePinSetup from "@/components/ForcePinSetup";
import GlobalDataSearch from "./GlobalDataSearch";
import {
  House, Factory, Package, Users, Truck, Wallet, ChartLine, UserGear, SignOut, List, IdentificationBadge, Receipt, DeviceMobile, Snowflake, Calculator, Wrench, QrCode, Gear, CaretLeft, CaretDown, Calendar, Coins, Trophy, Storefront, ClipboardText, Recycle, BookOpen, FileText, ClockCounterClockwise, MagnifyingGlass, Bell
} from "@phosphor-icons/react";

// Sidebar sections — each item has: to, label, icon, roles (null = all), testid
const NAV_SECTIONS = [
  {
    label: null,  // no section header for main dashboard
    items: [
      { to: "/", label: "Dashboard", icon: House, roles: null, testid: "nav-dashboard" },
      { to: "/scan", label: "Scan QR Pelanggan", icon: QrCode, roles: ["armada"], strictNoBypass: true, testid: "nav-scan" },
      { to: "/muatan", label: "Serah Terima Muatan", icon: Truck, roles: ["armada"], strictNoBypass: true, testid: "nav-muatan-armada", badgeMuatan: true },
      { to: "/repack-retur", label: "Repack & Retur", icon: Recycle, roles: ["armada"], strictNoBypass: true, testid: "nav-repack-retur" },
    ],
  },
  {
    label: "Divisi Produksi",
    items: [
      { to: "/biaya-pabrik", label: "Biaya Operasional Pabrik", icon: Receipt, roles: ["admin","produksi","checker"], testid: "nav-biaya-pabrik" },
      { to: "/produksi", label: "Produksi", icon: Factory, roles: ["admin","produksi","checker"], testid: "nav-produksi" },
      { to: "/laporan-produksi", label: "Rekap & Analitik Produksi", icon: ChartLine, roles: ["admin","produksi"], testid: "nav-produksi-rekap" },
      { to: "/shift", label: "Jadwal Shift", icon: Calendar, roles: ["admin","produksi"], testid: "nav-shift" },
      { to: "/bahan-baku", label: "Bahan Baku", icon: Package, roles: ["admin","produksi"], testid: "nav-bahan-baku" },
      { to: "/muatan", label: "Serah Terima Muatan", icon: Truck, roles: ["produksi","checker"], strictNoBypass: true, testid: "nav-muatan" },
      { to: "/repack-retur", label: "Repack & Retur", icon: Recycle, roles: ["checker"], strictNoBypass: true, testid: "nav-repack-retur-checker" },
      { to: "/penjualan-pabrik", label: "Penjualan Pabrik", icon: Storefront, roles: ["admin","produksi","checker"], testid: "nav-penjualan-pabrik" },
      { to: "/checklist", label: "Checklist Operasional", icon: ClipboardText, roles: ["admin","produksi","checker","auditor"], testid: "nav-checklist" },
      { to: "/repack", label: "Plastik Reject / Repack", icon: Recycle, roles: ["admin","produksi","checker","auditor"], testid: "nav-repack" },
      { to: "/mesin", label: "Master Mesin", icon: Wrench, roles: ["admin","produksi"], testid: "nav-mesin" },
      { to: "/maintenance/produksi", label: "Maintenance Mesin", icon: Wrench, roles: ["admin","produksi","checker"], testid: "nav-mtn-produksi" },
    ],
  },
  {
    label: "Divisi HRD",
    items: [
      { to: "/karyawan", label: "Karyawan & Absensi", icon: IdentificationBadge, roles: ["admin","hrd"], testid: "nav-karyawan" },
      { to: "/hr-attendance-report", label: "Laporan Absensi HRD", icon: ChartLine, roles: ["hrd"], strictNoBypass: true, testid: "nav-hr-attendance-report" },
      { to: "/payroll", label: "Payroll", icon: Coins, roles: ["admin","hrd"], testid: "nav-payroll" },
      { to: "/payroll-config", label: "Pengaturan Payroll", icon: Coins, roles: ["admin","hrd","akunting"], testid: "nav-payroll-config" },
      { to: "/shift", label: "Jadwal Shift (Lihat)", icon: Calendar, roles: ["hrd"], strictNoBypass: true, testid: "nav-shift-hrd" },
      { to: "/insentif", label: "Insentif Driver", icon: Coins, roles: ["hrd"], strictNoBypass: true, testid: "nav-insentif-hrd" },
      { to: "/hrd-pengajuan", label: "Pengajuan & Geofence", icon: List, roles: ["admin","hrd"], testid: "nav-hrd-pengajuan", correctionBadge: true },
      { to: "/hr-master", label: "Struktur HR", icon: IdentificationBadge, roles: ["hrd"], strictNoBypass: true, testid: "nav-hr-master-hrd" },
      { to: "/hr-policies", label: "Kebijakan & Peraturan", icon: BookOpen, roles: ["hrd"], strictNoBypass: true, testid: "nav-hr-policy-hrd" },
      { to: "/hr-contract-templates", label: "Template Kontrak", icon: ClipboardText, roles: ["hrd"], strictNoBypass: true, testid: "nav-hr-contract-template-hrd" },
      { to: "/hr-contracts", label: "Kontrak Karyawan", icon: FileText, roles: ["hrd"], strictNoBypass: true, testid: "nav-hr-contracts-hrd" },
      { to: "/hr-travel", label: "Perjalanan Dinas & Klaim", icon: Receipt, roles: ["hrd"], strictNoBypass: true, testid: "nav-hr-travel-hrd" },
      { to: "/hr-leave", label: "Cuti & Hak Karyawan", icon: Calendar, roles: ["hrd"], strictNoBypass: true, testid: "nav-hr-leave-hrd" },
      { to: "/hr-attendance-policy", label: "Kebijakan Kehadiran", icon: Calendar, roles: ["hrd"], strictNoBypass: true, testid: "nav-hr-attendance-policy-hrd" },
      { to: "/hr-timekeeping", label: "Keterlambatan & Pulang Awal", icon: ClockCounterClockwise, roles: ["hrd"], strictNoBypass: true, testid: "nav-hr-timekeeping-hrd" },
      { to: "/hr-overtime", label: "Lembur Karyawan", icon: ClockCounterClockwise, roles: ["hrd"], strictNoBypass: true, testid: "nav-hr-overtime-hrd" },
      { to: "/hr-reporting", label: "Laporan HR & Kesiapan Payroll", icon: ChartLine, roles: ["hrd"], strictNoBypass: true, testid: "nav-hr-reporting-hrd" },
    ],
  },
  {
    label: "Unit Simpan Pinjam (USP)",
    items: [
      { to: "/usp", label: "Keanggotaan & Pinjaman USP", icon: Coins, roles: ["usp","hrd","akunting","auditor"], testid: "nav-usp" },
    ],
  },
  {
    label: "Divisi Distribusi",
    items: [
      { to: "/distribusi", label: "Order & Armada", icon: Truck, roles: ["admin","distribusi"], testid: "nav-distribusi" },
      { to: "/laporan-distribusi", label: "Rekap & Analitik Distribusi", icon: ChartLine, roles: ["admin","distribusi"], testid: "nav-distribusi-rekap" },
      { to: "/pelanggan", label: "Pelanggan", icon: Users, roles: ["admin","distribusi"], testid: "nav-pelanggan" },
      { to: "/setoran", label: "Data Penjualan", icon: Receipt, roles: ["admin","distribusi","kasir","driver","armada"], testid: "nav-setoran" },
      { to: "/maintenance/armada", label: "Maintenance Armada", icon: Wrench, roles: ["admin","distribusi"], testid: "nav-mtn-armada" },
    ],
  },
  {
    label: "Inventory",
    items: [
      { to: "/inventory", label: "Inventory", icon: Package, roles: ["admin","produksi","distribusi","akunting","auditor"], testid: "nav-inventory" },
    ],
  },
  {
    label: "Laporan",
    items: [
      { to: "/laporan-umum", label: "Laporan Umum", icon: ChartLine, roles: ["admin","kasir","akunting","auditor"], testid: "nav-laporan-umum" },
      { to: "/laporan-produksi", label: "Laporan Produksi", icon: Factory, roles: ["admin","superadmin","produksi","kasir"], testid: "nav-laporan-produksi" },
      { to: "/laporan-distribusi", label: "Laporan Distribusi", icon: Truck, roles: ["admin","distribusi","kasir","akunting","auditor"], testid: "nav-laporan-distribusi" },
      { to: "/laporan-keuangan", label: "Laporan Keuangan", icon: Wallet, roles: ["admin","kasir","akunting","auditor"], testid: "nav-laporan-keuangan" },
      { to: "/kinerja-driver", label: "Kinerja Driver", icon: Trophy, roles: ["admin","distribusi","kasir","akunting","auditor","driver"], testid: "nav-kinerja-driver" },
    ],
  },
  {
    label: "Keuangan",
    items: [
      { to: "/keuangan", label: "Keuangan", icon: Wallet, roles: ["admin","kasir","akunting"], testid: "nav-keuangan" },
      { to: "/accounting", label: "Akunting & Buku Besar", icon: BookOpen, roles: ["admin","kasir","akunting","auditor"], testid: "nav-accounting" },
      { to: "/rekonsiliasi-penjualan", label: "Rekonsiliasi Penjualan", icon: ClipboardText, roles: ["admin","akunting","auditor"], testid: "nav-finance-reconciliation" },
      { to: "/piutang-pelanggan", label: "Piutang per Pelanggan", icon: Users, roles: ["admin","akunting","auditor"], testid: "nav-finance-debtors" },
      { to: "/hpp", label: "Komponen HPP", icon: Calculator, roles: ["admin","akunting","auditor"], testid: "nav-hpp" },
      { to: "/bbm-armada", label: "BBM Armada", icon: Receipt, roles: ["admin","kasir"], testid: "nav-bbm-armada" },
      { to: "/insentif", label: "Insentif Driver", icon: Coins, roles: ["admin","akunting","kasir","auditor","distribusi"], testid: "nav-insentif" },
    ],
  },
  {
    label: "Sistem",
    items: [
      { to: "/pemegang-saham", label: "Pemegang Saham & Dividen", icon: Coins, roles: ["__superadmin_only__"], testid: "nav-pemegang-saham" },
      { to: "/users", label: "Manajemen User", icon: UserGear, roles: ["__superadmin_only__"], testid: "nav-users" },
      { to: "/settings", label: "Pengaturan Aplikasi", icon: Gear, roles: ["__superadmin_only__"], testid: "nav-settings" },
      { to: "/hr-master", label: "Master HR GEMA", icon: IdentificationBadge, roles: ["__superadmin_only__"], testid: "nav-hr-master-superadmin" },
      { to: "/hr-policies", label: "Kebijakan GEMA", icon: BookOpen, roles: ["__superadmin_only__"], testid: "nav-hr-policy-superadmin" },
      { to: "/hr-contract-templates", label: "Template Kontrak", icon: ClipboardText, roles: ["__superadmin_only__"], testid: "nav-hr-contract-template-superadmin" },
      { to: "/hr-contracts", label: "Kontrak Karyawan", icon: FileText, roles: ["__superadmin_only__"], testid: "nav-hr-contracts-superadmin" },
      { to: "/hr-travel", label: "Travel Policy & Settlement", icon: Receipt, roles: ["__superadmin_only__"], testid: "nav-hr-travel-superadmin" },
      { to: "/hr-leave", label: "Master Cuti & Entitlement", icon: Calendar, roles: ["__superadmin_only__"], testid: "nav-hr-leave-superadmin" },
      { to: "/hr-attendance-policy", label: "Policy Kehadiran", icon: Calendar, roles: ["__superadmin_only__"], testid: "nav-hr-attendance-policy-superadmin" },
      { to: "/hr-timekeeping", label: "Timekeeping Evidence", icon: ClockCounterClockwise, roles: ["__superadmin_only__"], testid: "nav-hr-timekeeping-superadmin" },
      { to: "/hr-overtime", label: "Workforce Overtime", icon: ClockCounterClockwise, roles: ["__superadmin_only__"], testid: "nav-hr-overtime-superadmin" },
      { to: "/hr-reporting", label: "HR Payroll Readiness", icon: ChartLine, roles: ["__superadmin_only__"], testid: "nav-hr-reporting-superadmin" },
      { to: "/owner", label: "Owner Dashboard", icon: DeviceMobile, roles: ["__superadmin_only__"], testid: "nav-owner" },
    ],
  },
];

// HRD navigation groups mirror the task-oriented category pattern in GreatDay.
 // Reuse the canonical items so route permissions, badges, and test IDs stay intact.
 // This applies ONLY to role=hrd; the Founder-locked Superadmin sidebar stays unchanged.
const HRD_ITEMS = Object.fromEntries(
  NAV_SECTIONS.find(section => section.label === "Divisi HRD").items.map(item => [item.testid, item])
);
const hrdMenu = (...ids) => ids.map(id => HRD_ITEMS[id]);
const HRD_NAV_SECTIONS = [
  {
    label: null,
    items: [NAV_SECTIONS[0].items.find(item => item.testid === "nav-dashboard")],
  },
  {
    label: "Karyawan & Struktur",
    icon: IdentificationBadge,
    testid: "hrd-category-karyawan",
    items: hrdMenu("nav-karyawan", "nav-hr-master-hrd", "nav-hr-contracts-hrd"),
  },
  {
    label: "Kehadiran & Jadwal",
    icon: Calendar,
    testid: "hrd-category-kehadiran",
    items: hrdMenu("nav-shift-hrd", "nav-hr-timekeeping-hrd"),
  },
  {
    label: "Aktivitas & Pengajuan",
    icon: List,
    testid: "hrd-category-pengajuan",
    items: [...hrdMenu("nav-hrd-pengajuan", "nav-hr-leave-hrd", "nav-hr-overtime-hrd", "nav-hr-travel-hrd"),
      { to: "/usp", label: "Verifikasi Anggota & Pinjaman USP", icon: Coins, roles: ["hrd"], strictNoBypass: true, testid: "nav-usp-hrd" }],
  },
  {
    label: "Payroll & Insentif",
    icon: Coins,
    testid: "hrd-category-payroll",
    items: hrdMenu("nav-payroll", "nav-payroll-config", "nav-insentif-hrd"),
  },
  {
    label: "Laporan",
    icon: ChartLine,
    testid: "hrd-category-laporan",
    items: [
      ...hrdMenu("nav-hr-attendance-report"),
      { to: "/hr-lateness-report", label: "Laporan Keterlambatan & Denda", icon: ClockCounterClockwise,
        roles: ["hrd"], strictNoBypass: true, testid: "nav-hr-lateness-report" },
      ...hrdMenu("nav-hr-reporting-hrd"),
    ],
  },
  {
    label: "Pengaturan HR",
    icon: Gear,
    testid: "hrd-category-pengaturan",
    items: hrdMenu("nav-hr-attendance-policy-hrd", "nav-hr-policy-hrd", "nav-hr-contract-template-hrd"),
  },
];

// Akunting gets a task-oriented workspace instead of the cross-division menu.
// The legacy pages stay reachable while Finance parity is completed, but they
// no longer dominate the role's primary navigation.
// Kasir uses a flat, single-click navigation — no "Transaksi" accordion.
const KASIR_NAV_SECTIONS = [
  {
    label: null,
    items: [
      { to: "/", label: "Dashboard Kasir", icon: House, roles: ["kasir"], testid: "nav-dashboard" },
      { to: "/setoran", label: "Data Penjualan", icon: Receipt, roles: ["kasir"], testid: "nav-setoran" },
      { to: "/biaya-pabrik", label: "Review Biaya Pabrik", icon: Receipt, roles: ["kasir"], testid: "nav-biaya-pabrik-review" },
      { to: "/keuangan", label: "Penerimaan & Pembayaran", icon: Wallet, roles: ["kasir"], testid: "nav-keuangan" },
      { to: "/piutang-pelanggan", label: "Piutang per Pelanggan", icon: Users, roles: ["kasir"], testid: "nav-piutang-pelanggan" },
      { to: "/bbm-armada", label: "BBM Armada", icon: Receipt, roles: ["kasir"], testid: "nav-bbm-armada" },
    ],
  },
];

const ACCOUNTING_NAV_SECTIONS = [
  {
    label: null,
    items: [{ to: "/", label: "Dashboard Finance", icon: House, roles: ["akunting"], testid: "nav-dashboard" }],
  },
  {
    label: "Transaksi",
    items: [
      { to: "/accounting?tab=journals", label: "Jurnal Umum", icon: Receipt, roles: ["akunting"], testid: "nav-accounting-journals" },
      { to: "/accounting?tab=shadow", label: "Review Shadow (Nonresmi)", icon: ClipboardText, roles: ["akunting"], testid: "nav-accounting-shadow" },
    ],
  },
  {
    label: "Buku Besar",
    items: [
      { to: "/accounting?tab=accounts", label: "Daftar Akun", icon: BookOpen, roles: ["akunting"], testid: "nav-accounting-accounts" },
      { to: "/accounting?tab=periods", label: "Periode Akuntansi", icon: Calendar, roles: ["akunting"], testid: "nav-accounting-periods" },
    ],
  },
  {
    label: "Laporan",
    items: [
      { to: "/laporan-keuangan", label: "Laporan Keuangan", icon: ChartLine, roles: ["akunting"], testid: "nav-laporan-keuangan" },
      { to: "/buku-setoran-driver", label: "Buku Setoran Driver", icon: Calendar, roles: ["akunting"], testid: "nav-buku-setoran-driver" },
      { to: "/laporan-umum", label: "Laporan Operasional", icon: Factory, roles: ["akunting"], testid: "nav-laporan-umum" },
    ],
  },
  {
    label: "Konfigurasi",
    items: [
      { to: "/accounting?tab=rules", label: "Aturan Posting", icon: Gear, roles: ["akunting"], testid: "nav-accounting-rules" },
      { to: "/payroll-config", label: "Mapping Akun Payroll", icon: Coins, roles: ["akunting"], testid: "nav-payroll-config" },
      { to: "/biaya-pabrik", label: "Review Biaya Pabrik", icon: Receipt, roles: ["akunting"], testid: "nav-biaya-pabrik-review" },
      { to: "/hr-reporting", label: "Input HR Terverifikasi", icon: ChartLine, roles: ["akunting"], testid: "nav-hr-reporting-accounting" },
      { to: "/usp", label: "Laporan Keuangan USP (Terpisah)", icon: Coins, roles: ["akunting"], testid: "nav-usp-akunting" },
      { to: "/hr-travel", label: "Klaim Perjalanan Dinas", icon: Receipt, roles: ["akunting"], testid: "nav-hr-travel-accounting" },
      { to: "/hpp", label: "Komponen HPP", icon: Calculator, roles: ["akunting"], testid: "nav-hpp" },
    ],
  },
  {
    label: "Referensi Operasional",
    items: [
      { to: "/keuangan", label: "Keuangan Operasional (Legacy)", icon: Wallet, roles: ["akunting"], testid: "nav-keuangan" },
      { to: "/inventory", label: "Inventory", icon: Package, roles: ["akunting"], testid: "nav-inventory" },
    ],
  },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [appName, setAppName] = useState("GEMA");
  const [logoVer, setLogoVer] = useState(1);
  const [pendingMuatanRows, setPendingMuatanRows] = useState([]);
  const [dismissedMuatanIds, setDismissedMuatanIds] = useState({});
  const pendingMuatan = pendingMuatanRows.length;
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [openSections, setOpenSections] = useState({});
  const showBack = location.pathname !== "/";

  useEffect(() => {
    if (user?.role !== "armada") {
      setPendingMuatanRows([]);
      return;
    }
    const fetchPending = () => api.get("/muatan?status=pending")
      .then(r => setPendingMuatanRows(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
    fetchPending();
    const t = setInterval(fetchPending, 60000);
    window.addEventListener("muatan-changed", fetchPending);
    return () => { clearInterval(t); window.removeEventListener("muatan-changed", fetchPending); };
  }, [user?.role]);

  const pendingMuatanPopup = user?.role === "armada"
    ? pendingMuatanRows.find(m => !dismissedMuatanIds[m.id])
    : null;

  const handleMuatanPopup = async (row, action) => {
    if (!row?.id) return;
    let body;
    if (action === "reject") {
      const reason = (window.prompt("Alasan penolakan muatan (wajib):") || "").trim();
      if (!reason) return;
      body = { reason };
    }
    try {
      await api.post(`/muatan/${row.id}/${action}`, body);
      const { toast } = await import("sonner");
      toast.success(action === "approve" ? "Muatan diterima dan disinkronkan ke rute." : "Muatan ditolak dan dikembalikan ke Checker.");
      setDismissedMuatanIds(prev => ({ ...prev, [row.id]: true }));
      window.dispatchEvent(new Event("muatan-changed"));
    } catch (err) {
      const { toast } = await import("sonner");
      toast.error(err.response?.data?.detail || "Gagal memproses muatan");
    }
  };

  // WP2 UIX — pending correction badge for hrd/admin/superadmin. Fetch on
  // mount and on the custom refresh event; no aggressive polling. If the
  // fetch fails, badge simply stays hidden (never fake number, no toast).
  const [pendingCorr, setPendingCorr] = useState(0);
  useEffect(() => {
    const role = user?.role;
    if (!(role === "hrd" || role === "admin" || role === "superadmin")) return;
    let alive = true;
    const fetchCorr = async () => {
      try {
        const r = await api.get("/absensi/corrections?status=PENDING");
        if (alive) setPendingCorr(Array.isArray(r.data) ? r.data.length : 0);
      } catch { /* keep prior value; never surface */ }
    };
    fetchCorr();
    const h = () => fetchCorr();
    window.addEventListener("absensi-corrections-changed", h);
    const t = setInterval(fetchCorr, 5 * 60 * 1000); // gentle 5-min refresh
    return () => { alive = false; window.removeEventListener("absensi-corrections-changed", h); clearInterval(t); };
  }, [user?.role]);

  useEffect(() => {
    const load = () => api.get("/settings").then(r => { setAppName(r.data.app_name || "GEMA"); setLogoVer(r.data.logo_version || 1); }).catch(() => {});
    load();
    window.addEventListener("app-settings-changed", load);
    return () => window.removeEventListener("app-settings-changed", load);
  }, []);
  const logoSrc = `/logo-gema.png?v=${logoVer}`;
  const brandName = (appName || "GEMA").toUpperCase().includes("ERP") ? appName : `${appName} ERP`;

  const canSee = (item) => {
    const roles = item.roles;
    if (item.strictNoBypass) return !!(roles && roles.includes(user?.role));
    if (!roles) return true;
    if (user?.role === "superadmin") return true;
    if (roles.includes("__superadmin_only__")) return false;
    return roles.includes(user?.role);
  };

  const isCurrentNavItem = (to, routerActive) => {
    if (!to.includes("?")) return routerActive;
    const [pathname, search = ""] = to.split("?");
    const expectedTab = new URLSearchParams(search).get("tab");
    const currentTab = new URLSearchParams(location.search).get("tab");
    return location.pathname === pathname && currentTab === expectedTab;
  };

  const SUPERADMIN_SECTION_ORDER = [null, "Divisi Produksi", "Divisi Distribusi", "Keuangan", "Divisi HRD", "Unit Simpan Pinjam (USP)", "Inventory", "Laporan", "Sistem"];

  const activeSections = user?.role === "kasir"
    ? KASIR_NAV_SECTIONS
    : user?.role === "akunting"
      ? ACCOUNTING_NAV_SECTIONS
      : user?.role === "hrd"
        ? HRD_NAV_SECTIONS
        : user?.role === "superadmin"
      ? [...NAV_SECTIONS].sort((a, b) => {
          const ai = SUPERADMIN_SECTION_ORDER.indexOf(a.label);
          const bi = SUPERADMIN_SECTION_ORDER.indexOf(b.label);
          return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
        })
      : NAV_SECTIONS;
  const sectionDisplayLabel = (label) => {
    const labels = {
      "Divisi Produksi": "Produksi",
      "Divisi HRD": "HRD",
      "Divisi Distribusi": "Distribusi",
      "Keuangan": "Finance",
      "Sistem": "Master Data",
    };
    return labels[label] || label;
  };

  useEffect(() => {
    const matches = {};
    activeSections.forEach((section, idx) => {
      if (!section.label) return;
      const visible = section.items.filter(it => canSee(it));
      if (visible.some(it => {
        const [path] = it.to.split("?");
        return location.pathname === path || (path !== "/" && location.pathname.startsWith(path + "/"));
      })) {
        matches[idx] = true;
      }
    });
    if (Object.keys(matches).length) {
      setOpenSections(prev => ({ ...prev, ...matches }));
    }
  }, [location.pathname, user?.role]);

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  const NavList = () => (
    <nav className="flex flex-col gap-1 px-3 py-4">
      {activeSections.map((section, sIdx) => {
        const visibleItems = section.items.filter(it => canSee(it));
        if (visibleItems.length === 0) return null;

        if (!section.label) {
          return (
            <div key={sIdx}>
              {visibleItems.map(({ to, label, icon: Icon, testid, badgeMuatan, correctionBadge }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={to === "/"}
                  data-testid={testid}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `sidebar-link flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium ${isCurrentNavItem(to, isActive) ? "active" : ""}`
                  }
                >
                  <Icon size={18} weight="duotone" />
                  <span className="flex-1">{label}</span>
                  {badgeMuatan && pendingMuatan > 0 && (
                    <span data-testid="muatan-pending-badge" className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center">{pendingMuatan}</span>
                  )}
                  {correctionBadge && pendingCorr > 0 && (
                    <span data-testid="corrections-pending-badge" className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0">{pendingCorr > 99 ? "99+" : pendingCorr}</span>
                  )}
                </NavLink>
              ))}
            </div>
          );
        }

        const isOpen = !!openSections[sIdx];
        const groupActive = visibleItems.some(it => {
          const [path] = it.to.split("?");
          return location.pathname === path || (path !== "/" && location.pathname.startsWith(path + "/"));
        });
        const GroupIcon = section.icon || visibleItems[0]?.icon || List;

        return (
          <div key={sIdx} className="sidebar-group">
            <button
              type="button"
              data-testid={section.testid}
              className={`sidebar-group-button ${groupActive ? "group-active" : ""}`}
              onClick={() => setOpenSections(prev => ({ ...prev, [sIdx]: !prev[sIdx] }))}
              aria-expanded={isOpen}
            >
              <GroupIcon size={19} weight="duotone"/>
              <span>{sectionDisplayLabel(section.label)}</span>
              <CaretDown size={14} className={`ml-auto transition-transform ${isOpen ? "rotate-180" : ""}`}/>
            </button>
            {isOpen && (
              <div className="sidebar-group-children">
                {visibleItems.map(({ to, label, icon: Icon, testid, badgeMuatan, correctionBadge }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={to === "/"}
                    data-testid={testid}
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      `sidebar-child-link ${isCurrentNavItem(to, isActive) ? "active" : ""}`
                    }
                  >
                    <Icon size={16} weight="duotone"/>
                    <span className="flex-1">{label}</span>
                    {badgeMuatan && pendingMuatan > 0 && (
                      <span data-testid="muatan-pending-badge" className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center">{pendingMuatan}</span>
                    )}
                    {correctionBadge && pendingCorr > 0 && (
                      <span data-testid="corrections-pending-badge" className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0">{pendingCorr > 99 ? "99+" : pendingCorr}</span>
                    )}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );

  return (
    <div className="erp-shell min-h-screen flex bg-[#F4F7F9]">
      {/* Desktop Sidebar */}
      <aside className="sidebar-navy erp-sidebar hidden lg:flex lg:w-60 lg:flex-col lg:fixed lg:inset-y-0">
        <div className="px-5 py-5 border-b border-white/10 flex items-center gap-3">
          <img src={logoSrc} alt={appName} className="erp-brand-logo w-11 h-11 rounded-lg shrink-0 object-cover"/>
          <div className="min-w-0">
            <h1 className="font-heading text-lg font-extrabold text-white tracking-tight leading-tight" data-testid="brand-app-name">{brandName}</h1>
            <div className="text-[10px] text-cyan-300 tracking-[0.2em] uppercase leading-tight">Operations Console</div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          <NavList />
        </div>
        <div className="erp-sidebar-ornament" aria-hidden="true">
          <span className="erp-sidebar-wave erp-sidebar-wave-a"/>
          <span className="erp-sidebar-wave erp-sidebar-wave-b"/>
          <span className="erp-sidebar-wave erp-sidebar-wave-c"/>
          <span className="erp-sidebar-glow"/>
        </div>
        <div className="erp-sidebar-status-wrap px-4 pb-5">
          <div className="erp-sidebar-status-card">
            <div className="erp-sidebar-status-title"><span/> Operational</div>
            <div className="erp-sidebar-status-meta">Ledger: Shadow · Recon: Off</div>
          </div>
        </div>
      </aside>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex" onClick={() => setMobileOpen(false)}>
          <div className="absolute inset-0 bg-black/50" />
          <div className="sidebar-navy erp-sidebar relative flex flex-col w-60 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-5 border-b border-white/10 shrink-0 flex items-center gap-3">
              <img src={logoSrc} alt={appName} className="erp-brand-logo w-10 h-10 rounded-lg shrink-0 object-cover"/>
              <div>
                <h1 className="font-heading text-base font-bold text-white leading-tight">{brandName}</h1>
                <div className="text-[10px] text-cyan-300 tracking-[0.2em] uppercase leading-tight">Operations Console</div>
              </div>
            </div>
            <NavList />
            <div className="border-t border-white/10 px-4 py-4 mt-auto shrink-0">
              <button data-testid="logout-btn-mobile" onClick={handleLogout} className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-md bg-white/5 text-slate-200 hover:bg-white/10">
                <SignOut size={16}/> Keluar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="erp-main-frame flex-1 lg:ml-60 lg:flex-none flex flex-col min-w-0">
        <header className="erp-topbar erp-topbar-v4 sticky top-0 z-30 border-b px-4 md:px-7 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <button data-testid="mobile-menu-btn" className="lg:hidden p-2 rounded-xl hover:bg-slate-100" onClick={() => setMobileOpen(true)}>
              <List size={22} />
            </button>
            {showBack && (
              <button data-testid="back-btn" onClick={() => nav(-1)} className="p-2 rounded-xl hover:bg-slate-100 text-slate-700" title="Kembali">
                <CaretLeft size={20} weight="bold"/>
              </button>
            )}
            <GlobalDataSearch />
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <button type="button" className="erp-icon-button relative" title="Notifikasi">
              <Bell size={20} weight="duotone"/>
              {(pendingCorr + pendingMuatan) > 0 && <span className="erp-notification-dot"/>}
            </button>
            <div className="relative">
              <button
                type="button"
                className="erp-user-menu flex items-center gap-2.5 rounded-xl px-2 py-1.5"
                onClick={(e) => { e.stopPropagation(); setUserMenuOpen(v => !v); }}
                aria-expanded={userMenuOpen}
                data-testid="topbar-user-menu"
              >
                <div className="erp-avatar">
                  {(user?.name || "U").split(" ").slice(0,2).map(x => x[0]).join("").toUpperCase()}
                </div>
                <div className="hidden md:block leading-tight text-left">
                  <div className="text-sm font-bold text-slate-900 max-w-[150px] truncate">{user?.name}</div>
                  <div className="text-[10px] uppercase tracking-[0.12em] text-slate-400">{ROLE_LABEL[user?.role] || user?.role}</div>
                </div>
                <CaretDown size={14} className={`hidden md:block text-slate-500 transition-transform ${userMenuOpen ? "rotate-180" : ""}`}/>
              </button>
              {userMenuOpen && (
                <div className="erp-user-dropdown" onPointerDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
                  <div className="erp-user-dropdown-head">
                    <div className="font-semibold text-slate-900 truncate">{user?.name}</div>
                    <div className="text-xs text-slate-500 truncate">{user?.email}</div>
                  </div>
                  {!user?.email_verified && (
                    <button
                      type="button"
                      data-testid="send-verify-btn"
                      onClick={async ()=>{ try { await api.post("/auth/send-verification"); alert("Email verifikasi dikirim. Cek inbox Anda."); } catch(err){ alert(err.response?.data?.detail || "Gagal"); } }}
                      className="erp-user-dropdown-action"
                    >
                      Verifikasi Email
                    </button>
                  )}
                  <button
                    type="button"
                    data-testid="switch-account-btn"
                    onClick={handleLogout}
                    className="erp-user-dropdown-action"
                  >
                    <UserGear size={16}/> Ganti Akun
                  </button>
                  <button type="button" data-testid="logout-btn" onClick={handleLogout} className="erp-user-dropdown-action erp-user-dropdown-logout">
                    <SignOut size={16}/> Keluar
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>
        <main className="erp-content-area flex-1 p-4 md:p-8 max-w-full">
          <div className="erp-outlet-frame">
            <Suspense fallback={<div role="status" className="p-6 text-sm text-slate-600">Membuka menu...</div>}><Outlet key={`${location.pathname}${location.search}`} /></Suspense>
          </div>
        </main>
      </div>
      {pendingMuatanPopup && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4" data-testid="armada-muatan-popup">
          <div className="absolute inset-0 bg-slate-950/45" onClick={() => setDismissedMuatanIds(prev => ({ ...prev, [pendingMuatanPopup.id]: true }))}/>
          <div className="relative w-full max-w-md rounded-2xl border border-amber-200 bg-white p-5 shadow-2xl">
            <div className="text-xs font-bold uppercase tracking-[0.16em] text-amber-600">Serah Terima Muatan</div>
            <div className="mt-1 font-heading text-xl font-bold text-slate-900">Muatan baru dari Checker</div>
            <div className="mt-3 rounded-xl bg-amber-50 p-3 text-sm text-slate-700">
              <div className="font-semibold">{pendingMuatanPopup.kode_armada || user?.armada_code || "Armada"} · {pendingMuatanPopup.bag_muatan || 0} bag</div>
              <div className="mt-1 text-xs text-slate-500">Tanggal {pendingMuatanPopup.tanggal || "-"} · Checker {pendingMuatanPopup.checker_by || "-"}</div>
              {pendingMuatanPopup.catatan && <div className="mt-2 text-xs">{pendingMuatanPopup.catatan}</div>}
            </div>
            <div className="mt-3 text-xs leading-relaxed text-slate-500">Cocokkan jumlah fisik. Selama fase uji coba, penjualan tetap bisa dilanjutkan meskipun approval belum dilakukan.</div>
            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <button type="button" onClick={() => setDismissedMuatanIds(prev => ({ ...prev, [pendingMuatanPopup.id]: true }))} className="px-3 py-2 rounded-lg border border-slate-300 text-sm font-semibold text-slate-600">Nanti</button>
              <button type="button" onClick={() => handleMuatanPopup(pendingMuatanPopup, "reject")} className="px-3 py-2 rounded-lg bg-red-50 text-red-700 text-sm font-semibold">Tolak</button>
              <button type="button" onClick={() => handleMuatanPopup(pendingMuatanPopup, "approve")} className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold">Terima Muatan</button>
            </div>
          </div>
        </div>
      )}
      <ForcePinSetup/>
    </div>
  );
}
